import { CircleCheck, LoaderCircle } from "lucide-react";
import { useHistory, useLocation } from "react-router";

import { Warning } from "@/components/icons/svg/figma/Warning";

import { isAiChatPath, useAiChatActivity } from "./AiChatActivityContext";

export type AiChatNavStatusKind = "running" | "ready" | "stopped" | "failed";

export type AiChatNavStatus = {
  kind: AiChatNavStatusKind;
  label: string;
  title?: string;
  viewId?: string;
};

function runningLabel(running: number, ready: number): string {
  const working =
    running > 1 ? `Working on ${running} responses` : "Working on a response";
  return ready > 0 ? `${working} · ${ready} ready` : `${working}…`;
}

// Running beats ready, which beats stopped or failed. The nav stays quiet on
// Freshpaint AI pages, where chat history carries each chat's status.
export function useAiChatNavStatus(): AiChatNavStatus | null {
  const { runningIds, ready, readyDismissed, failed } = useAiChatActivity();
  const { pathname } = useLocation();
  if (isAiChatPath(pathname)) return null;

  const visibleReady = readyDismissed ? 0 : ready.length;
  if (runningIds.size > 0) {
    return {
      kind: "running",
      label: runningLabel(runningIds.size, visibleReady),
    };
  }
  if (visibleReady > 0) {
    return visibleReady > 1
      ? {
          kind: "ready",
          label: `${visibleReady} responses ready`,
          viewId: ready[0].id,
        }
      : {
          kind: "ready",
          label: "Response ready",
          title: ready[0].title,
          viewId: ready[0].id,
        };
  }
  if (failed.length > 0) {
    const f = failed[0];
    return {
      kind: f.outcome,
      label: f.outcome === "stopped" ? "Response stopped" : "Response failed",
      title: f.title,
      viewId: f.id,
    };
  }
  return null;
}

function StatusIcon({
  kind,
  className,
}: {
  kind: AiChatNavStatusKind;
  className: string;
}) {
  if (kind === "running") {
    return (
      <LoaderCircle
        className={`${className} text-blue-300 motion-safe:animate-spin`}
        aria-hidden="true"
      />
    );
  }
  if (kind === "ready") {
    return (
      <CircleCheck
        className={`${className} text-green-500`}
        aria-hidden="true"
      />
    );
  }
  return (
    <Warning className={`${className} text-yellow-400`} aria-hidden="true" />
  );
}

// Sits on the corner of the nav icon. Its disc matches the item's background
// so it reads as a cutout in the icon rather than a dark circle. The item is
// only ever selected on Freshpaint AI pages, where no badge shows, so the
// shell and hover colors are the only two it needs.
export function AiChatNavBadge({ kind }: { kind: AiChatNavStatusKind }) {
  return (
    <span
      data-testid="ai-chat-nav-badge"
      data-kind={kind}
      aria-hidden="true"
      className="bg-nav-shell group-hover/navitem:bg-nav-hover absolute -top-[7px] -right-2 grid size-4 place-items-center rounded-full"
    >
      {kind === "ready" ? (
        <span className="size-2 rounded-full bg-green-500" />
      ) : (
        <StatusIcon kind={kind} className="size-3" />
      )}
    </span>
  );
}

export function AiChatNavPanel({
  status,
  onDone,
}: {
  status: AiChatNavStatus;
  onDone: () => void;
}) {
  const { dismissReady } = useAiChatActivity();
  const history = useHistory();
  const heading = (
    <span className="flex items-center gap-2 text-[13px] font-medium">
      <StatusIcon kind={status.kind} className="size-3.5 shrink-0" />
      {status.label}
    </span>
  );

  if (status.kind === "running") return heading;

  return (
    <div className="flex min-w-[240px] flex-col gap-2.5 whitespace-normal">
      {heading}
      {status.title && (
        <span className="-mt-1.5 line-clamp-2 max-w-[280px] pl-[22px] text-xs text-white/70">
          {status.title}
        </span>
      )}
      <span className="flex gap-2 pl-[22px]">
        <button
          type="button"
          className="h-7 cursor-pointer rounded-md bg-white px-2.5 text-xs font-medium text-gray-900 hover:bg-gray-100"
          onClick={() => {
            onDone();
            history.push(`/ai-chat/${status.viewId}`);
          }}
        >
          View
        </button>
        {status.kind === "ready" && (
          <button
            type="button"
            className="h-7 cursor-pointer rounded-md px-2.5 text-xs font-medium text-white ring-1 ring-white/30 ring-inset hover:bg-white/10"
            onClick={() => {
              onDone();
              dismissReady();
            }}
          >
            Dismiss
          </button>
        )}
      </span>
    </div>
  );
}

// Screen-reader announcement for the nav status. It stays mounted (empty when
// idle) because a live region inserted together with its text is often not
// announced, and it lives outside the nav's role="menu" lists, which may only
// contain menu items.
export function AiChatNavLiveRegion() {
  const status = useAiChatNavStatus();
  const text = status
    ? status.title
      ? `${status.label}: ${status.title}`
      : status.label
    : "";
  return (
    <span role="status" className="sr-only">
      {text}
    </span>
  );
}
